import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSession } from './SessionProvider'
import './RequireSession.css'

/**
 * The gate.
 *
 * Four outcomes, deliberately not two:
 *
 *   - **authenticated** → render the app.
 *   - **anonymous** → the login screen, remembering where they were going.
 *   - **step_up** → the login screen too, but asking for the RIGHT factor. Ory
 *     answers 403 for a session that exists and has not reached the required
 *     assurance level, and an ordinary sign-in cannot satisfy that (#98).
 *   - **unavailable** → say the identity provider is down. This is the one that
 *     matters. Collapsing it into "anonymous" sends a signed-in customer to a
 *     login screen during an Ory outage; they sign in, it fails, they try
 *     again, and the incident gets reported as "login is broken". The backend
 *     takes the same position for the same reason (§6).
 *
 * It renders nothing on `loading` rather than flashing the login screen — a
 * signed-in customer seeing "Sign in" for 200ms on every page load is the kind
 * of thing that erodes trust in a wallet specifically.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { state, refresh } = useSession()
  const location = useLocation()

  if (state.status === 'loading') {
    return (
      <div className="gate" role="status" aria-live="polite">
        <span className="gate__mark">TESORA</span>
      </div>
    )
  }

  if (state.status === 'unavailable') {
    return (
      <div className="gate gate--failed" role="alert">
        <span className="gate__mark">TESORA</span>
        <h1>We cannot check who you are right now.</h1>
        <p>{state.reason}</p>
        <p className="gate__hint">
          This is a fault at the identity provider, not a sign-out — your session has not been
          ended. Retrying is safe.
        </p>
        <button type="button" className="gate__retry" onClick={() => void refresh()}>
          Try again
        </button>
      </div>
    )
  }

  // SIGNED IN, AND NOT STRONGLY ENOUGH (#98).
  //
  // Sent to the login screen with the step-up carried in router state, which is
  // the channel `AuthPage` already reads — it passes `stepUp` to `flowDriver`,
  // which creates the flow with `{aal: 'aal2'}` rather than `{refresh: ...}`.
  // Without that parameter Ory answers `400 session_already_available` to a
  // request that already carries a valid session, so the plain login this used
  // to fall into could not have worked even in principle.
  if (state.status === 'step_up') {
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{ from: location.pathname + location.search, stepUp: state.stepUp }}
      />
    )
  }

  if (state.status === 'anonymous') {
    // `replace` so the back button does not walk into the gate again, and the
    // intended destination is carried so a deep link survives signing in.
    return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  }

  return <>{children}</>
}
